import React from 'react';
import {
  CalculatorIcon,
  DocumentMagnifyingGlassIcon,
  CheckBadgeIcon,
  GlobeAltIcon,
  BuildingOffice2Icon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useTranslation, Trans } from 'react-i18next';
import { trackEvent } from '../lib/metaPixel';
import { BUILDER_URL } from '../lib/externalLinks';

const Builder = () => {
  const { t } = useTranslation();

  const points = [
    {
      icon: CalculatorIcon,
      title: t('builder.points.computed.title'),
      description: t('builder.points.computed.description')
    },
    {
      icon: DocumentMagnifyingGlassIcon,
      title: t('builder.points.explained.title'),
      description: t('builder.points.explained.description')
    },
    {
      icon: CheckBadgeIcon,
      title: t('builder.points.noOverlap.title'),
      description: t('builder.points.noOverlap.description')
    }
  ];

  const audiences = [
    {
      icon: GlobeAltIcon,
      title: t('builder.audience.diaspora.title'),
      description: t('builder.audience.diaspora.description')
    },
    {
      icon: BuildingOffice2Icon,
      title: t('builder.audience.local.title'),
      description: t('builder.audience.local.description')
    }
  ];

  return (
    <section id="builder" className="py-12 sm:py-16 lg:py-20 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 right-0 w-1/3 h-1/3 bg-csl-green/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16 animate-slide-up">
          <div className="inline-flex items-center px-3 py-1.5 bg-csl-green/10 rounded-full text-csl-green font-semibold text-xs sm:text-sm mb-4 sm:mb-6">
            {t('builder.eyebrow')}
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            <Trans i18nKey="builder.title" components={{ 1: <span className="text-csl-green" /> }} />
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            {t('builder.description')}
          </p>
        </div>

        {/* What makes the recommendation trustworthy */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {points.map((point, index) => (
            <div
              key={point.title}
              className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 animate-slide-up hover:-translate-y-2"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-csl-green/10 rounded-xl sm:rounded-2xl flex items-center justify-center text-csl-green mb-4 sm:mb-6">
                <point.icon className="h-6 w-6 sm:h-8 sm:w-8" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{point.title}</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                {point.description}
              </p>
            </div>
          ))}
        </div>

        {/* Who it is for + the call to action */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl sm:rounded-3xl p-6 sm:p-8 md:p-12 shadow-xl border border-gray-100 animate-slide-up [animation-delay:450ms]">
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8 sm:mb-12">
            {t('builder.audienceTitle')}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-12">
            {audiences.map((audience) => (
              <div key={audience.title} className="flex items-start">
                <div className="w-12 h-12 bg-csl-green/10 rounded-full flex items-center justify-center text-csl-green mr-4 flex-shrink-0">
                  <audience.icon className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{audience.title}</h4>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    {audience.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <a
              href={BUILDER_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('Lead', { customData: { content_name: 'builder-section' } })}
              className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-csl-green text-white font-semibold rounded-xl hover:bg-csl-dark-green transition-all duration-300 shadow-lg hover:shadow-xl group hover:-translate-y-1 hover:scale-105 active:scale-95 text-sm sm:text-base w-full sm:w-auto text-center"
            >
              <span className="text-center">{t('builder.cta')}</span>
              <ArrowRightIcon className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform flex-shrink-0" />
            </a>
            <p className="text-sm text-gray-500 mt-4 max-w-2xl mx-auto">
              {t('builder.note')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Builder;
